import XLSXLib from 'xlsx-js-style';

const XLSX = XLSXLib.utils ? XLSXLib : (XLSXLib.default || XLSXLib);

// ─── Color Palette ───────────────────────────────────────────────────────────
const COLORS = {
  // Title row (Row 1, A1 merged across all columns)
  titleBg:   '1E3A5F',  // Deep navy blue
  titleFont: 'FFFFFF',  // Pure white

  // Excel Table Header row (Row 2)
  headerBg:     '1565C0',  // Vivid professional blue
  headerFont:   'FFFFFF',  // White bold
  headerBorder: '0D47A1',  // Darker blue border

  // Data rows (Row 3 onwards) - alternating even/odd
  evenBg:     'F0F6FC',  // Soft ice/light steel blue
  oddBg:      'FFFFFF',  // Pure white
  dataFont:   '1E293B',  // Slate dark
  cellBorder: 'CBD5E1',  // Subtle slate border
};

// ─── Helper: build a 4-sided thin-border object ─────────────────────────────
function fullBorder(colorHex) {
  const side = { style: 'thin', color: { rgb: colorHex } };
  return { top: side, bottom: side, left: side, right: side };
}

// ─── Helper: safely set or merge style on a worksheet cell ──────────────────
function styleCell(ws, cellRef, style) {
  if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
  ws[cellRef].s = style;
}

// ─── Helper: 0-based column index → Excel column letter (0->A, 25->Z, 26->AA)
function colLetter(idx) {
  let letter = '';
  let n = idx;
  do {
    letter = String.fromCharCode(65 + (n % 26)) + letter;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letter;
}

// ─── Helper: Format YYYY-MM-DD → DD-MM-YYYY ─────────────────────────────────
function formatD(d) {
  if (!d) return '';
  const clean = String(d).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return clean.split('-').reverse().join('-');
  }
  return clean;
}

/**
 * Exports tabular data to a professionally styled .xlsx workbook.
 *
 * Requirements fulfilled:
 * 1. A1 is merged across all columns with the filter/report title and date range.
 * 2. Excel table header is formatted with blue background and white font.
 * 3. Data rows alternate with even/odd color combination and full borders.
 * 4. Backward compatible with both options object or legacy [headers, ...rows] array.
 *
 * @param {object|any[][]} optionsOrData
 * @param {string}   [optionsOrData.title]     - Title / filter name (e.g. "Total Pending Domestic Help Verification")
 * @param {string}   [optionsOrData.dateRange] - Formatted date string (e.g. "Date From: 01-01-2026 To: 31-01-2026")
 * @param {string}   [optionsOrData.sdate]     - Start date from date filter
 * @param {string}   [optionsOrData.edate]     - End date from date filter
 * @param {string[]} [optionsOrData.headers]   - Column header labels array
 * @param {any[][]}  [optionsOrData.data]      - 2D data rows array
 * @param {string}   [optionsOrData.fileName]  - File name without .xlsx
 * @param {string}   [optionsOrData.sheetName] - Sheet tab name (default 'Report')
 * @param {string}   [fallbackFileName]        - Used if legacy array was passed
 */
export function exportToExcel(optionsOrData, fallbackFileName = 'Export') {
  let title = 'Report';
  let dateRange = '';
  let sdate = '';
  let edate = '';
  let headers = [];
  let data = [];
  let fileName = fallbackFileName;
  let sheetName = 'Report';

  // Handle legacy array signature: exportToExcel([headers, ...data], 'filename')
  if (Array.isArray(optionsOrData)) {
    if (optionsOrData.length > 0) {
      headers = optionsOrData[0] || [];
      data = optionsOrData.slice(1) || [];
    }
    title = fallbackFileName.replace(/[_-]+/g, ' ');
  } else if (optionsOrData && typeof optionsOrData === 'object') {
    title = optionsOrData.title || 'Report';
    dateRange = optionsOrData.dateRange || '';
    sdate = optionsOrData.sdate || '';
    edate = optionsOrData.edate || '';
    headers = optionsOrData.headers || [];
    data = optionsOrData.data || [];
    fileName = optionsOrData.fileName || fallbackFileName || 'Export';
    sheetName = optionsOrData.sheetName || 'Report';
  }

  // Safety fallback if headers is empty
  const numCols = Math.max(headers.length, (data[0] && data[0].length) || 1);
  const lastColLetter = colLetter(numCols - 1);

  // ── 1. Resolve date range from params if not explicitly formatted ─────────
  let effectiveDateRange = dateRange;
  if (!effectiveDateRange && (sdate || edate)) {
    const fromStr = formatD(sdate) || 'All';
    const toStr = formatD(edate) || 'All';
    effectiveDateRange = `Date From: ${fromStr}  To: ${toStr}`;
  }

  // Combine title with date range in row A1
  let a1Title = title;
  if (effectiveDateRange && !a1Title.includes(effectiveDateRange)) {
    a1Title = `${title}  (${effectiveDateRange})`;
  }

  // ── 2. Build AOA (Array of Arrays) ────────────────────────────────────────
  // Row 1 (Index 0): Title banner (A1 will be merged across all columns)
  // Row 2 (Index 1): Column Headers
  // Row 3+ (Index 2+): Data Rows
  const aoa = [
    [a1Title, ...Array(numCols - 1).fill('')],
    [...headers],
    ...data.map((row) => [...row]),
  ];

  // ── 3. Create worksheet from AOA ──────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // ── 4. Merges: Row 1 (A1 to LastCol1) ─────────────────────────────────────
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
  ];

  // ── 5. Apply Cell Styles ──────────────────────────────────────────────────

  // ── 5A. Title row (Row 1: A1...LastCol1) ─────────────────────────────────
  const titleStyle = {
    font: {
      bold: true,
      sz: 13,
      color: { rgb: COLORS.titleFont },
      name: 'Arial',
    },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: COLORS.titleBg },
    },
    alignment: {
      horizontal: 'center',
      vertical: 'center',
      wrapText: true,
    },
    border: fullBorder(COLORS.titleBg),
  };

  for (let c = 0; c < numCols; c++) {
    styleCell(ws, `${colLetter(c)}1`, titleStyle);
  }

  // ── 5B. Column Headers (Row 2: A2...LastCol2) ────────────────────────────
  const headerStyle = {
    font: {
      bold: true,
      sz: 11,
      color: { rgb: COLORS.headerFont },
      name: 'Arial',
    },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: COLORS.headerBg },
    },
    alignment: {
      horizontal: 'center',
      vertical: 'center',
      wrapText: true,
    },
    border: fullBorder(COLORS.headerBorder),
  };

  for (let c = 0; c < numCols; c++) {
    styleCell(ws, `${colLetter(c)}2`, headerStyle);
  }

  // ── 5C. Data rows (Row 3 onwards) ────────────────────────────────────────
  data.forEach((row, rowIdx) => {
    const isEven = rowIdx % 2 === 0;
    const excelRow = rowIdx + 3; // 1-based index (Row 1=Title, Row 2=Headers, Row 3=Data)

    for (let c = 0; c < numCols; c++) {
      const cellVal = row[c];
      const isNum = typeof cellVal === 'number' || (!isNaN(cellVal) && cellVal !== '' && cellVal !== null && typeof cellVal !== 'boolean');

      const dataCellStyle = {
        font: {
          sz: 10,
          color: { rgb: COLORS.dataFont },
          name: 'Arial',
        },
        fill: {
          patternType: 'solid',
          fgColor: { rgb: isEven ? COLORS.evenBg : COLORS.oddBg },
        },
        border: fullBorder(COLORS.cellBorder),
        alignment: {
          vertical: 'center',
          horizontal: isNum || c === 0 ? 'center' : 'left',
          wrapText: false,
        },
      };

      styleCell(ws, `${colLetter(c)}${excelRow}`, dataCellStyle);
    }
  });

  // ── 6. Set Row Heights ────────────────────────────────────────────────────
  ws['!rows'] = [
    { hpt: 36 },                          // Row 1 (Title): generous 36pt height
    { hpt: 26 },                          // Row 2 (Headers): 26pt height
    ...data.map(() => ({ hpt: 20 })),     // Row 3+ (Data): comfortable 20pt height
  ];

  // ── 7. Column Widths (Auto-sized based on content + header) ───────────────
  const colWidths = [headers, ...data].reduce((acc, row) => {
    row.forEach((cell, colIdx) => {
      const cellLen = cell !== null && cell !== undefined ? String(cell).length : 0;
      acc[colIdx] = Math.max(acc[colIdx] || 12, cellLen + 3);
    });
    return acc;
  }, []);

  // Clamp column widths between 12 and 45 characters
  ws['!cols'] = colWidths.map((w) => ({ wch: Math.min(Math.max(w, 12), 45) }));

  // ── 8. Sheet Range Coverage ───────────────────────────────────────────────
  ws['!ref'] = `A1:${lastColLetter}${aoa.length}`;

  // ── 9. Generate & Trigger Excel Download ──────────────────────────────────
  const cleanSheetName = (sheetName || 'Report').replace(/[*?:/\\\[\]]/g, ' ').slice(0, 31);
  const cleanFileName = (fileName || 'export').replace(/[/\\?%*:|"<>]/g, '_');

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, cleanSheetName);
  XLSX.writeFile(workbook, `${cleanFileName}.xlsx`);
}
